import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { appRoutes, programRoutes } from '@/modules/shared/constants/routes';
import { useAppVersion } from '@/modules/shared/hooks/useAppVersion';
import bqLogo from '@/images/bq-logo.png';

interface SidebarFooterProps {
  /** Program context for legal page links */
  programId?: string;
  /** Whether sidebar is in collapsed/icon-only mode */
  collapsed?: boolean;
  /** Additional className for the container */
  className?: string;
  /** @deprecated Logout removed from sidebar footer. Kept for backward compat – ignored. */
  onLogout?: () => void;
}

/**
 * Formats the app version string with a "v" prefix.
 */
function formatAppVersion(version?: string | null): string {
  if (!version) return 'v unavailable';
  const cleanVersion = String(version).trim();
  if (!cleanVersion) return 'v unavailable';
  return cleanVersion.toLowerCase().startsWith('v')
    ? cleanVersion
    : `v${cleanVersion}`;
}

/**
 * SidebarFooter — Shared compact footer for all sidebar variants.
 *
 * Renders:
 *   Terms & Conditions | Privacy Policy
 *   v2.x.xxx | ©2026 EPPREM
 *   Powered by [BioQuest Logo]
 */
export function SidebarFooter({
  programId,
  collapsed = false,
  className,
}: SidebarFooterProps) {
  const year = new Date().getFullYear();
  const { version } = useAppVersion();

  const privacyPath = programId
    ? programRoutes.legalPage(programId, 'privacy-policy')
    : appRoutes.legalPage('privacy-policy');

  const termsPath = programId
    ? programRoutes.legalPage(programId, 'terms-and-conditions')
    : appRoutes.legalPage('terms-and-conditions');

  const formattedVersion = formatAppVersion(version);

  // ── Collapsed: minimal footer ────────────────────────────────
  if (collapsed) {
    return (
      <div
        className={cn(
          'sidebar-footer flex-shrink-0',
          'sidebar-footer--collapsed',
          className,
        )}
      />
    );
  }

  // ── Expanded: full footer ────────────────────────────────────
  return (
    <footer
      className={cn(
        'sidebar-footer flex-shrink-0',
        className,
      )}
    >
      {/* Legal links */}
      <div className="sidebar-footer-links">
        <Link to={termsPath} target="_blank">
          Terms &amp; Conditions
        </Link>
        <span className="sidebar-footer-separator" aria-hidden="true">|</span>
        <Link to={privacyPath} target="_blank">
          Privacy Policy
        </Link>
      </div>

      {/* Version + Copyright */}
      <div className="sidebar-footer-meta">
        <span>{formattedVersion}</span>
        <span className="sidebar-footer-separator" aria-hidden="true">|</span>
        <span>©{year} EPPREM</span>
      </div>

      {/* Powered by */}
      <div className="sidebar-footer-powered">
        <span>Powered by</span>
        <img src={bqLogo} alt="BioQuest" className="bioquest-logo" />
      </div>
    </footer>
  );
}
